/**
 * usePresence - Hook for managing presence state and publishing editing events
 *
 * Automatically publishes presence:editing events when the active tab changes,
 * and provides helpers for tracking who's editing which request.
 */

import { useEffect, useCallback, useRef, useMemo } from 'react';
import { useStore } from '../../stores';
import { useAuth } from '../../lib/auth/AuthProvider';
import { useCollaborationContext } from '../../lib/collaboration';
import { eventClient } from '../../lib/collaboration/eventClient';

interface UsePresenceOptions {
  enabled?: boolean;
}

interface UsePresenceReturn {
  // Current user's editing state
  currentEditingRequestId: string | null;
  // All active users and what they're editing
  activeUsers: Map<string, { userId: string; userName?: string; editingRequestId?: string }>;
  // Get users editing a specific request (excluding self)
  getUsersEditingRequest: (requestId?: string | null) => Array<{
    userId: string;
    userName?: string;
    editingRequestId?: string;
  }>;
  // Manually publish editing state (for when user explicitly focuses)
  publishEditing: (requestId: string | null) => void;
}

export function usePresence(options: UsePresenceOptions = {}): UsePresenceReturn {
  const { enabled = true } = options;
  const { user } = useAuth();
  const { activeUsers } = useCollaborationContext();

  // Get current tab state
  const activeTabId = useStore((state) => state.activeTabId);
  const tabs = useStore((state) => state.tabs);
  const activeWorkspaceId = useStore((state) => state.activeWorkspaceId);

  // Track the last published request ID to avoid duplicate publishes
  const lastPublishedRequestId = useRef<string | null>(null);
  const publishTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get the current tab's request ID
  const currentTab = useMemo(
    () => tabs.find((tab) => tab.id === activeTabId),
    [tabs, activeTabId]
  );
  const currentEditingRequestId = currentTab?.requestId || null;

  // Workspace ID for publishing
  const workspaceId = activeWorkspaceId || 'local-workspace';

  // Publish editing state with debounce
  const publishEditing = useCallback(
    (requestId: string | null) => {
      if (!enabled || !user?.userId) return;

      // Clear any pending publish
      if (publishTimeoutRef.current) {
        clearTimeout(publishTimeoutRef.current);
      }

      // Debounce rapid changes (100ms)
      publishTimeoutRef.current = setTimeout(() => {
        // Skip if same as last published
        if (requestId === lastPublishedRequestId.current) return;

        lastPublishedRequestId.current = requestId;

        eventClient.publishPresence(workspaceId, 'editing', {
          editingRequestId: requestId || undefined,
          userName: user.username,
        });
      }, 100);
    },
    [enabled, user?.userId, user?.username, workspaceId]
  );

  // Auto-publish when active tab changes
  useEffect(() => {
    if (!enabled) return;

    publishEditing(currentEditingRequestId);

    // Cleanup: clear editing state when component unmounts
    return () => {
      if (publishTimeoutRef.current) {
        clearTimeout(publishTimeoutRef.current);
      }
    };
  }, [enabled, currentEditingRequestId, publishEditing]);

  // Get users editing a specific request (excluding self)
  const getUsersEditingRequest = useCallback(
    (requestId?: string | null) => {
      if (!requestId) return [];

      const usersEditing: Array<{
        userId: string;
        userName?: string;
        editingRequestId?: string;
      }> = [];

      activeUsers.forEach((userData, key) => {
        // Exclude self
        if (user?.userId && userData.userId === user.userId) return;
        // Check if editing this request
        if (userData.editingRequestId === requestId) {
          usersEditing.push(userData);
        }
      });

      return usersEditing;
    },
    [activeUsers, user?.userId]
  );

  return {
    currentEditingRequestId,
    activeUsers,
    getUsersEditingRequest,
    publishEditing,
  };
}
